import React, { useState } from "react";
import PropTypes from "prop-types";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CatalogNativeSelect from "./CatalogNativeSelect";
import { getFeedColumnLabel } from "./utils/catalogMappingTableUtils";
import { normalizeRowSkipRules } from "./utils/mappingRulesUtils";
import { CM_TOOLTIPS } from "./utils/tooltipsContent";
import {
  CELL_MATCH_MODE_OPTIONS,
  formatMatchModeLabel,
  normalizeCellMatchMode,
} from "./utils/cellMatchModeUtils";

const RowSkipRulesPanel = ({
  columnNumbers,
  headerLabels,
  isHeadless,
  rules,
  onChange,
  disabled = false,
  className = "",
  showMatchesOnly = false,
  onShowMatchesOnlyChange,
}) => {
  const [selectedColumn, setSelectedColumn] = useState("");
  const [ruleValue, setRuleValue] = useState("");
  const [matchMode, setMatchMode] = useState("eq");
  const normalizedRules = normalizeRowSkipRules(rules);

  const columnOptions = columnNumbers.map((column) => ({
    value: column,
    label: getFeedColumnLabel(column, headerLabels, isHeadless),
  }));

  const handleAddRule = () => {
    if (disabled) {
      return;
    }
    if (!selectedColumn) {
      return;
    }
    const normalizedMode = normalizeCellMatchMode(matchMode);
    const nextRule = {
      column: selectedColumn,
      value: ruleValue == null ? "" : String(ruleValue),
      mode: normalizedMode,
    };
    const exists = normalizedRules.some(
      (rule) => rule.column === nextRule.column
        && rule.value === nextRule.value
        && (rule.mode || "eq") === nextRule.mode,
    );
    if (exists) {
      return;
    }
    onChange([...normalizedRules, nextRule]);
    setRuleValue("");
  };

  const handleRemoveRule = (index) => {
    if (disabled) {
      return;
    }
    onChange(normalizedRules.filter((_, ruleIndex) => ruleIndex !== index));
  };

  return (
    <div className={`cm-row-skip-panel${className ? ` ${className}` : ""}`.trim()}>

        <div className="cm-row-skip-form">
          <span className="cm-row-skip-heading" aria-hidden="true">
            <span>SKIP</span>
            <span>ROWS</span>
          </span>
          <CatalogNativeSelect
            className="cm-row-skip-selector"
            value={selectedColumn}
            onChange={setSelectedColumn}
            options={columnOptions}
            placeholder="Colonna"
            ariaLabel="Colonna skip rule"
            disabled={disabled}
          />
          <CatalogNativeSelect
            className="cm-cell-match-mode-select"
            value={matchMode}
            onChange={setMatchMode}
            options={CELL_MATCH_MODE_OPTIONS}
            placeholder="Mode"
            ariaLabel="Modalita confronto skip rule"
            disabled={disabled}
          />
          <input
            type="text"
            className="cm-input cm-row-skip-field"
            placeholder="Valore cella (vuoto = null/empty)"
            value={ruleValue}
            disabled={disabled}
            onChange={(event) => setRuleValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleAddRule();
              }
            }}
          />
          <button
            type="button"
            className="cm-btn cm-btn-secondary cm-row-skip-add-btn"
            onClick={handleAddRule}
            disabled={disabled || !selectedColumn}
            title={CM_TOOLTIPS.aggiungiSkipRule}
          >
            Aggiungi Regola
          </button>
          <button
            type="button"
            className={`cm-btn cm-btn-secondary cm-tool-matches-btn${showMatchesOnly ? " is-active" : ""}`}
            onClick={() => onShowMatchesOnlyChange?.(!showMatchesOnly)}
            disabled={disabled || normalizedRules.length === 0 || !onShowMatchesOnlyChange}
            title="Mostra solo righe/celle che corrispondono alle regole SKIP ROWS"
          >
            Solo matches
          </button>
        </div>

      {normalizedRules.length > 0 && (
        <div className="cm-row-skip-rules">
          {normalizedRules.map((rule, index) => (
            <div key={`${rule.column}-${rule.value}-${index}`} className="cm-row-skip-card">
              <span className="cm-row-skip-card-label">
                {getFeedColumnLabel(rule.column, headerLabels, isHeadless)}
                {" "}
                {formatMatchModeLabel(rule.mode)}
                {" "}
                {String(rule.value ?? "").trim() === "" ? "(vuoto/null)" : `"${rule.value}"`}
              </span>
              <IconButton
                size="small"
                aria-label="Rimuovi regola skip"
                title={CM_TOOLTIPS.rimuoviSkipRule}
                onClick={() => handleRemoveRule(index)}
                disabled={disabled}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

RowSkipRulesPanel.propTypes = {
  columnNumbers: PropTypes.arrayOf(PropTypes.string).isRequired,
  headerLabels: PropTypes.object,
  isHeadless: PropTypes.bool,
  rules: PropTypes.arrayOf(PropTypes.shape({
    column: PropTypes.string,
    value: PropTypes.string,
    mode: PropTypes.string,
  })),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  showMatchesOnly: PropTypes.bool,
  onShowMatchesOnlyChange: PropTypes.func,
};

export default RowSkipRulesPanel;
